class ImagePeekCarouselCursor {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;

    root.addEventListener('pointermove', this.handlePointerMove);
    root.addEventListener('pointerleave', this.handlePointerLeave);
  }

  clear() {
    this.root.classList.remove('is-hover-left', 'is-hover-right');
  }

  /** @type {(event: PointerEvent) => void} */
  handlePointerMove = (event) => {
    if (event.pointerType === 'touch') return;
    if (!(event.target instanceof Element)) return;

    const slideEl = event.target.closest('slideshow-slide');
    if (!slideEl || slideEl.getAttribute('aria-hidden') === 'false') {
      this.clear();
      return;
    }

    const currentSlideEl = this.root.querySelector('slideshow-slide[aria-hidden="false"]');
    if (!currentSlideEl) {
      this.clear();
      return;
    }

    const isLeft = slideEl.getBoundingClientRect().left < currentSlideEl.getBoundingClientRect().left;
    this.root.classList.toggle('is-hover-left', isLeft);
    this.root.classList.toggle('is-hover-right', !isLeft);
  };

  handlePointerLeave = () => this.clear();
}

/**
 * Moves the slideshow to its intended starting slide.
 * The slideshow-component's own `initial-slide` attribute has a bug: it pre-sets its
 * internal "current" index to match the target before asking itself to scroll there,
 * so the scroll request sees "we're already there" and does nothing. Selecting the
 * slide ourselves (while current is still untouched at 0) avoids that early-return.
 * @param {Element} root
 */
function goToInitialSlide(root) {
  const targetId = root.getAttribute('data-initial-select-id');
  if (!targetId) return;
  if (!('select' in root) || typeof root.select !== 'function') return;

  root.select({ id: targetId }, undefined, { animate: false });
}

/**
 * Enables continuous looping. Leading/trailing "clone" slides (marked with
 * data-clone-of) show the same image as the real slide at the opposite end of the
 * list, so there's always something to peek at and click past the last/first real
 * slide. Once a clone becomes current, silently hand off to its real counterpart so
 * navigation continues from a slide that has real neighbors on both sides.
 * @param {Event} event
 */
function handleCloneSnapBack(event) {
  const root = event.currentTarget;
  if (!root || !('select' in root) || typeof root.select !== 'function') return;

  const detail = /** @type {{ slide?: Element }} */ ('detail' in event ? event.detail : undefined);
  const cloneOf = detail?.slide?.getAttribute('data-clone-of');
  if (!cloneOf) return;

  root.select({ id: cloneOf }, undefined, {});
}

function initImagePeekCarousels() {
  document.querySelectorAll('.image-peek-carousel:not([data-nav-initialized])').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    root.setAttribute('data-nav-initialized', '');
    new ImagePeekCarouselCursor(root);
    root.addEventListener('slideshow:select', handleCloneSnapBack);
    requestAnimationFrame(() => requestAnimationFrame(() => goToInitialSlide(root)));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initImagePeekCarousels);
} else {
  initImagePeekCarousels();
}

document.addEventListener('shopify:section:load', initImagePeekCarousels);
